import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { therapistTestimonialsQuery } from "@/api-queries/homepage-testimonials";
import { defineSection } from "@/ssr/define-section";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

function QuoteView() {
  const ref = useScrollReveal<HTMLElement>();
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Admin-curated in Dashboard → Sadržaj → Testimoniali (placement "Za terapeute").
  const { data } = useQuery(therapistTestimonialsQuery);
  const en = i18n.language === "en";
  const reviews = (data?.testimonials ?? []).map((r) => ({
    id: r.id,
    author: r.name,
    role: en ? r.roleEn || r.roleBs : r.roleBs,
    shortText: en ? r.quoteEn : r.quoteBs,
    fullText: en ? r.fullQuoteEn || r.fullQuoteBs : r.fullQuoteBs,
  }));

  const current = reviews[Math.min(active, reviews.length - 1)];
  if (!current) return null;

  function showReview(index: number) {
    setActive(index);
    setDialogOpen(false);
  }

  function goToPrevious() {
    showReview((active - 1 + reviews.length) % reviews.length);
  }

  function goToNext() {
    showReview((active + 1) % reviews.length);
  }

  return (
    <section
      ref={ref}
      className="animate-on-scroll relative overflow-hidden py-20 sm:py-32"
      style={{ background: "oklch(100% 0 0)" }}
    >
      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <h2
          className="font-heading font-black leading-[0.95] tracking-[-0.035em] text-foreground"
          style={{ fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)" }}
        >
          {t("forTherapists.quote.titlePrefix")}{" "}
          <em className="font-normal italic" style={{ color: "var(--color-ther-clay)" }}>
            {t("forTherapists.quote.titleHighlight")}
          </em>
        </h2>

        <div className="mt-12 min-h-[240px] sm:min-h-[260px]" aria-live="polite">
          <blockquote
            key={active}
            className="font-heading text-[clamp(1.35rem,3vw,2.25rem)] font-medium leading-[1.32] tracking-tight text-foreground"
          >
            {current.shortText}
          </blockquote>

          {current.fullText && (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="mt-8 inline-flex items-center justify-center rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition hover:border-[var(--color-ther-clay)] hover:text-[var(--color-ther-clay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ther-clay)] focus-visible:ring-offset-2"
            >
              {t("forTherapists.quote.readFull")}
            </button>
          )}
        </div>

        <figcaption className="mt-10">
          <div className="font-heading text-sm font-semibold text-foreground">
            {current.author}
          </div>
          <div className="mt-1 text-[13px] text-muted-foreground">
            {current.role}
          </div>
        </figcaption>

        {reviews.length > 1 && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={goToPrevious}
              className="inline-flex size-10 items-center justify-center rounded-full border border-border text-foreground transition hover:border-[var(--color-ther-clay)] hover:text-[var(--color-ther-clay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ther-clay)] focus-visible:ring-offset-2"
              aria-label={t("forTherapists.quote.previous")}
            >
              <CaretLeft className="size-4" weight="bold" aria-hidden />
            </button>

            <div className="flex items-center gap-2">
              {reviews.map((review, index) => {
                const isActive = index === active;
                return (
                  <button
                    key={review.id}
                    type="button"
                    onClick={() => showReview(index)}
                    className="h-2.5 rounded-full transition-all"
                    style={{
                      width: isActive ? "28px" : "10px",
                      background: isActive ? "var(--color-ther-clay)" : "var(--color-border)",
                    }}
                    aria-label={t("forTherapists.quote.goTo", { name: review.author })}
                    aria-current={isActive ? "true" : undefined}
                  />
                );
              })}
            </div>

            <button
              type="button"
              onClick={goToNext}
              className="inline-flex size-10 items-center justify-center rounded-full border border-border text-foreground transition hover:border-[var(--color-ther-clay)] hover:text-[var(--color-ther-clay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ther-clay)] focus-visible:ring-offset-2"
              aria-label={t("forTherapists.quote.next")}
            >
              <CaretRight className="size-4" weight="bold" aria-hidden />
            </button>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} className="sm:max-w-3xl">
        <DialogHeader onClose={() => setDialogOpen(false)}>
          <DialogTitle>{current.author}</DialogTitle>
          <DialogDescription>{current.role}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="whitespace-pre-line text-left text-base leading-8 text-foreground sm:text-lg">
            {current.fullText}
          </p>
        </DialogBody>
      </Dialog>
    </section>
  );
}

export const Quote = defineSection({
  Component: QuoteView,
  queries: [therapistTestimonialsQuery],
});
